import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useContent } from '../../context/ContentContext';
import { formatRupiah } from '../../utils/formatters';
import { resolveMediaUrl, getApiUrl } from '../../utils/api';
import {
  getCurrentCustomerProfile,
  getCustomerOrdersRpc,
  getSupabase,
  getCustomerSessionToken,
  CUSTOMER_PROFILE_KEY,
} from '../../utils/supabase';
import {
  getCustomerLoyalty,
  CustomerLoyaltyData,
} from '../../utils/supabaseLoyalty';
import {
  getMembershipTierSettings,
  calculateMembershipTier,
  isOrderValidForTier,
  fetchCustomerSuccessfulReferralsCount,
  CalculatedCustomerTier,
  DEFAULT_MEMBERSHIP_TIER_SETTINGS,
} from '../../utils/supabaseMembershipTier';
import {
  ChevronRight,
  ChevronDown,
  Bell,
  ShoppingBag,
  Search,
  Star,
  Coffee,
  Coins,
  Leaf,
  Croissant,
  IceCream,
  SlidersHorizontal,
  Heart,
  Plus,
  Store,
  Motorbike,
  Package,
} from 'lucide-react';
import { MenuItem, CustomerProfile, CustomerOrder, OrderType } from '../../types';
import { SpecialForYouSection } from './SpecialForYouSection';

interface HeroSectionProps {
  onOpenOrder?: (item?: MenuItem, initialOrderType?: OrderType) => void;
  onOpenMember?: () => void;
  onNavigateTab?: (tab: 'home' | 'pesanan' | 'outlet' | 'menu' | 'akun') => void;
  customerProfile?: CustomerProfile | null;
}

const DEFAULT_PRODUCT_IMAGE =
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1000&q=80';

function getInitialCachedLoyalty(): CustomerLoyaltyData {
  if (typeof window === 'undefined') {
    return { pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 };
  }
  try {
    const token = getCustomerSessionToken();
    const raw = localStorage.getItem(CUSTOMER_PROFILE_KEY);
    if (token && raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.pointsBalance === 'number') {
        return {
          pointsBalance: parsed.pointsBalance,
          totalPointsEarned: Number(parsed.totalPointsEarned || 0),
          totalPointsRedeemed: Number(parsed.totalPointsRedeemed || 0),
        };
      }
    }
  } catch {}
  return { pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 };
}

function getInitialCachedProfile(): CustomerProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const token = getCustomerSessionToken();
    const raw = localStorage.getItem(CUSTOMER_PROFILE_KEY);
    if (token && raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.id || parsed.userId || parsed.namaLengkap || parsed.nomorHp)) {
        return parsed as CustomerProfile;
      }
    }
  } catch {}
  return null;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  onOpenOrder,
  onOpenMember,
  onNavigateTab,
  customerProfile: externalCustomerProfile,
}) => {
  const { data } = useContent();
  const { menuCategories, menuItems } = data;

  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(() =>
    externalCustomerProfile !== undefined ? externalCustomerProfile : getInitialCachedProfile()
  );
  const [, setIsSessionChecking] = useState<boolean>(() =>
    Boolean(getCustomerSessionToken())
  );
  const [loyaltyData, setLoyaltyData] = useState<CustomerLoyaltyData>(() =>
    getInitialCachedLoyalty()
  );
  const [tierData, setTierData] = useState<CalculatedCustomerTier>(() =>
    calculateMembershipTier(0, 0, DEFAULT_MEMBERSHIP_TIER_SETTINGS)
  );
  const [, setRecentOrders] = useState<CustomerOrder[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [favoriteIds, setFavoriteIds] = useState<Record<string, boolean>>({});
  const lastKnownTokenRef = useRef<string | null>(getCustomerSessionToken());

  // Global Delivery Sync State from backend /api/delivery/status
  const [globalDeliveryStatus, setGlobalDeliveryStatus] = useState<boolean | 'UNKNOWN'>('UNKNOWN');
  const [isCheckingDeliveryStatus, setIsCheckingDeliveryStatus] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    const fetchDeliveryStatus = async () => {
      try {
        const res = await fetch(getApiUrl('/api/delivery/status'), { cache: 'no-store' });
        const d = await res.json().catch(() => null);
        if (res.ok && d && typeof d.enabled === 'boolean') {
          if (isMounted) {
            setGlobalDeliveryStatus(d.enabled);
            try {
              localStorage.setItem('leton_global_delivery_enabled', JSON.stringify(d.enabled));
            } catch {}
          }
          return;
        }
      } catch {}

      // Fallback: Read directly from Supabase public.leton_content (id = 'global_delivery_settings')
      try {
        const client = getSupabase();
        const { data: row, error } = await client
          .from('leton_content')
          .select('content')
          .eq('id', 'global_delivery_settings')
          .maybeSingle();

        if (!error) {
          let resolvedEnabled = true;
          if (row && row.content) {
            const c = typeof row.content === 'string' ? JSON.parse(row.content) : row.content;
            if (c && typeof c.enabled === 'boolean') {
              resolvedEnabled = c.enabled;
            }
          }
          if (isMounted) {
            setGlobalDeliveryStatus(resolvedEnabled);
            try {
              localStorage.setItem('leton_global_delivery_enabled', JSON.stringify(resolvedEnabled));
            } catch {}
          }
          return;
        }
      } catch {}

      if (isMounted) {
        setGlobalDeliveryStatus('UNKNOWN');
      }
    };

    fetchDeliveryStatus().finally(() => {
      if (isMounted) {
        setIsCheckingDeliveryStatus(false);
      }
    });

    const handleGlobalDeliveryUpdated = (e: Event) => {
      const customEvt = e as CustomEvent<{ enabled?: boolean }>;
      if (customEvt.detail && typeof customEvt.detail.enabled === 'boolean') {
        setGlobalDeliveryStatus(customEvt.detail.enabled);
        setIsCheckingDeliveryStatus(false);
      } else {
        fetchDeliveryStatus();
      }
    };

    window.addEventListener('leton_global_delivery_updated', handleGlobalDeliveryUpdated);
    const timer = setInterval(fetchDeliveryStatus, 25000);
    return () => {
      isMounted = false;
      window.removeEventListener('leton_global_delivery_updated', handleGlobalDeliveryUpdated);
      clearInterval(timer);
    };
  }, []);

  // Sync customer profile, loyalty balance, tier, and recent orders
  const syncCustomerHomepageData = useCallback(async () => {
    try {
      const currentToken = getCustomerSessionToken();
      lastKnownTokenRef.current = currentToken;

      if (!currentToken) {
        setCustomerProfile(null);
        setIsSessionChecking(false);
        setLoyaltyData({ pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 });
        setTierData(calculateMembershipTier(0, 0, DEFAULT_MEMBERSHIP_TIER_SETTINGS));
        setRecentOrders([]);
        return;
      }

      const profile = await getCurrentCustomerProfile().catch(() => null);
      lastKnownTokenRef.current = getCustomerSessionToken();
      setCustomerProfile(profile);

      if (!profile) {
        setIsSessionChecking(false);
        setLoyaltyData({ pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 });
        setTierData(calculateMembershipTier(0, 0, DEFAULT_MEMBERSHIP_TIER_SETTINGS));
        setRecentOrders([]);
        return;
      }

      if (typeof profile.pointsBalance === 'number') {
        setLoyaltyData((prev) => ({
          pointsBalance: profile.pointsBalance ?? prev.pointsBalance,
          totalPointsEarned: profile.totalPointsEarned ?? prev.totalPointsEarned,
          totalPointsRedeemed: profile.totalPointsRedeemed ?? prev.totalPointsRedeemed,
        }));
      }

      setIsSessionChecking(false);

      const targetUserId = profile?.id || profile?.userId || '';
      const targetPhone = profile?.nomorHp || '';

      if (targetUserId || targetPhone) {
        const [loyalty, tierSettingsRes, rpcOrdersRes, refCount] = await Promise.all([
          getCustomerLoyalty(targetUserId, targetPhone).catch(() => ({
            pointsBalance: 0,
            totalPointsEarned: 0,
            totalPointsRedeemed: 0,
          })),
          getMembershipTierSettings().catch(() => ({
            settings: DEFAULT_MEMBERSHIP_TIER_SETTINGS,
          })),
          getCustomerOrdersRpc().catch(() => ({ success: false, orders: [] })),
          targetUserId
            ? fetchCustomerSuccessfulReferralsCount(targetUserId).catch(() => 0)
            : Promise.resolve(0),
        ]);

        setLoyaltyData(loyalty);

        let mappedOrders: CustomerOrder[] = [];
        if (rpcOrdersRes.success && Array.isArray(rpcOrdersRes.orders)) {
          mappedOrders = rpcOrdersRes.orders.map((o: any) => ({
            id: o.id,
            orderNumber: o.order_number || o.orderNumber || o.id,
            outletId: o.outlet_id || o.outletId || '',
            outletName: o.outlet_name || o.outletName || 'LetonKu Dumai',
            customerName: o.customer_name || o.customerName || profile?.namaLengkap || '',
            customerPhone: o.customer_phone || o.customerPhone || targetPhone,
            customerId: o.customer_id || o.customerId || targetUserId,
            orderType: o.order_type || o.orderType || 'TAKE AWAY',
            tableNumber: o.table_number || o.tableNumber || '',
            items: Array.isArray(o.items) ? o.items : [],
            totalAmount: Number(o.total_amount ?? o.totalAmount ?? 0),
            paymentMethod: o.payment_method || o.paymentMethod || 'QRIS',
            paymentStatus: o.payment_status || o.paymentStatus || 'WAITING PAYMENT',
            orderStatus: o.order_status || o.orderStatus || 'NEW',
            createdAt: o.created_at || o.createdAt || new Date().toISOString(),
          }));
        }

        setRecentOrders(mappedOrders);

        const validOrders = mappedOrders.filter(isOrderValidForTier);
        const validOrderCount = validOrders.length;
        const validSpending = validOrders.reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0);
        setTierData(
          calculateMembershipTier(
            validOrderCount,
            validSpending,
            refCount,
            tierSettingsRes.settings || DEFAULT_MEMBERSHIP_TIER_SETTINGS
          )
        );
      }
    } catch (err) {
      console.warn('[Homepage] Sync customer data notice:', err);
      setIsSessionChecking(false);
    }
  }, []);

  // Immediately reflect login/logout/profile updates from parent App state
  useEffect(() => {
    if (externalCustomerProfile === undefined) return;
    if (!externalCustomerProfile) {
      if (!getCustomerSessionToken()) {
        setCustomerProfile(null);
        setLoyaltyData({ pointsBalance: 0, totalPointsEarned: 0, totalPointsRedeemed: 0 });
        setTierData(calculateMembershipTier(0, 0, DEFAULT_MEMBERSHIP_TIER_SETTINGS));
        setRecentOrders([]);
      }
      return;
    }
    setCustomerProfile(externalCustomerProfile);
    if (typeof externalCustomerProfile.pointsBalance === 'number') {
      setLoyaltyData((prev) => ({
        pointsBalance: externalCustomerProfile.pointsBalance ?? prev.pointsBalance,
        totalPointsEarned: externalCustomerProfile.totalPointsEarned ?? prev.totalPointsEarned,
        totalPointsRedeemed: externalCustomerProfile.totalPointsRedeemed ?? prev.totalPointsRedeemed,
      }));
    }
    syncCustomerHomepageData();
  }, [
    externalCustomerProfile?.id,
    externalCustomerProfile?.userId,
    externalCustomerProfile?.namaLengkap,
    externalCustomerProfile?.pointsBalance,
    syncCustomerHomepageData,
  ]);

  useEffect(() => {
    syncCustomerHomepageData();

    const handleRefresh = () => {
      syncCustomerHomepageData();
    };

    window.addEventListener('focus', handleRefresh);
    window.addEventListener('storage', handleRefresh);
    return () => {
      window.removeEventListener('focus', handleRefresh);
      window.removeEventListener('storage', handleRefresh);
    };
  }, [syncCustomerHomepageData]);

  // Realtime subscription for customer points and order status updates
  useEffect(() => {
    const targetUserId = customerProfile?.id || customerProfile?.userId;
    if (!targetUserId) return;

    try {
      const client = getSupabase();
      const channel = client
        .channel(`homepage_customer_sync_${targetUserId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customer_points',
            filter: `customer_id=eq.${targetUserId}`,
          },
          () => {
            syncCustomerHomepageData();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            filter: `customer_id=eq.${targetUserId}`,
          },
          () => {
            syncCustomerHomepageData();
          }
        )
        .subscribe();

      return () => {
        client.removeChannel(channel);
      };
    } catch (err) {
      console.warn('[Homepage] Realtime subscription notice:', err);
    }
  }, [customerProfile?.id, customerProfile?.userId, syncCustomerHomepageData]);

  // Sorted categories
  const sortedCategories = useMemo(() => {
    return [...(menuCategories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [menuCategories]);

  // Items for "Popular Near You"
  const favoriteItems = useMemo(() => {
    const available = (menuItems || []).filter((item) => item && item.isAvailable !== false);
    const byCategory =
      selectedCategory === 'all'
        ? available
        : available.filter((item) => item.categoryId === selectedCategory);
    const q = searchQuery.trim().toLowerCase();
    const bySearch = q
      ? byCategory.filter(
          (item) =>
            item.name.toLowerCase().includes(q) ||
            (item.description || '').toLowerCase().includes(q)
        )
      : byCategory;
    return bySearch.sort((a, b) => (a.order || 0) - (b.order || 0)).slice(0, 8);
  }, [menuItems, selectedCategory, searchQuery]);

  const handleOrderCta = (item?: MenuItem, initialOrderType?: OrderType) => {
    if (onOpenOrder) onOpenOrder(item, initialOrderType);
  };

  const scrollToMenuSection = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (onNavigateTab) {
      onNavigateTab('menu');
      return;
    }
    handleOrderCta();
  };

  const branches = Array.isArray(data.branches) ? data.branches : [];

  const customerDisplayName = useMemo(() => {
    const rawName = (customerProfile?.namaLengkap || '').trim();
    if (!rawName) return '';
    return rawName.split(/\s+/)[0];
  }, [customerProfile?.namaLengkap]);

  const selectedOutletHeaderLabel = useMemo(() => {
    try {
      const saved = localStorage.getItem('leton_selected_outlet');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.name) return parsed.name;
      }
    } catch {}
    if (branches.length > 0 && branches[0]?.branchName) {
      return `Leton ${branches[0].branchName}`;
    }
    return 'Leton Sudirman Central';
  }, [branches]);

  const handleOpenMemberOrAccount = () => {
    if (customerProfile && onNavigateTab) {
      onNavigateTab('akun');
    } else if (onOpenMember) {
      onOpenMember();
    } else if (onNavigateTab) {
      onNavigateTab('akun');
    }
  };

  const getCategoryIcon = (name: string, idx: number) => {
    const lower = name.toLowerCase();
    if (lower.includes('coffee') && !lower.includes('non')) {
      return <Coffee className="w-3.5 h-3.5" />;
    }
    if (lower.includes('tea') || lower.includes('matcha') || lower.includes('non')) {
      return <Leaf className="w-3.5 h-3.5" />;
    }
    if (lower.includes('pastry') || lower.includes('snack') || lower.includes('food')) {
      return <Croissant className="w-3.5 h-3.5" />;
    }
    if (idx % 3 === 1) return <Leaf className="w-3.5 h-3.5" />;
    if (idx % 3 === 2) return <Croissant className="w-3.5 h-3.5" />;
    return <IceCream className="w-3.5 h-3.5" />;
  };

  const actualPointsBalance = Number(
    loyaltyData?.pointsBalance ?? customerProfile?.pointsBalance ?? 0
  );
  const displayPoints = (
    Number.isFinite(actualPointsBalance) ? Math.max(0, actualPointsBalance) : 0
  ).toLocaleString('en-US');

  const heroBanners = useMemo(() => {
    const activePromoBanners = Array.isArray(data.promoBanners)
      ? [...data.promoBanners]
          .filter((b) => b && b.isActive !== false && b.imageUrl)
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      : [];

    if (activePromoBanners.length > 0) {
      return activePromoBanners.map((b, idx) => ({
        id: b.id || `hero-slide-${idx}`,
        imageUrl: resolveMediaUrl(b.imageUrl) || DEFAULT_PRODUCT_IMAGE,
        alt: b.title || `LetonKu Banner ${idx + 1}`,
      }));
    }

    if (data.siteSettings?.heroBgImage) {
      const resolvedHero = resolveMediaUrl(data.siteSettings.heroBgImage);
      if (resolvedHero) {
        return [
          {
            id: 'hero-bg-primary',
            imageUrl: resolvedHero,
            alt: data.siteSettings.brandName || 'LetonKu Banner',
          },
        ];
      }
    }

    return [
      {
        id: 'hero-default-coffee',
        imageUrl: DEFAULT_PRODUCT_IMAGE,
        alt: 'LetonKu Specialty Brew',
      },
    ];
  }, [data.promoBanners, data.siteSettings?.heroBgImage, data.siteSettings?.brandName]);

  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  useEffect(() => {
    if (currentSlide >= heroBanners.length) {
      setCurrentSlide(0);
    }
  }, [heroBanners.length, currentSlide]);

  useEffect(() => {
    if (heroBanners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroBanners.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [heroBanners.length]);

  const handleBannerTouchStart = (e: React.TouchEvent) => {
    if (e.touches && e.touches[0]) {
      setTouchStartX(e.touches[0].clientX);
    }
  };

  const handleBannerTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null || heroBanners.length <= 1) {
      setTouchStartX(null);
      return;
    }
    const endX = e.changedTouches[0]?.clientX ?? touchStartX;
    const deltaX = endX - touchStartX;
    if (Math.abs(deltaX) > 40) {
      if (deltaX < 0) {
        setCurrentSlide((prev) => (prev + 1) % heroBanners.length);
      } else {
        setCurrentSlide((prev) => (prev - 1 + heroBanners.length) % heroBanners.length);
      }
    }
    setTouchStartX(null);
  };

  return (
    <div id="home" className="w-full bg-white text-[#0A192F] pb-24 max-w-lg mx-auto overflow-x-hidden">
      <main id="main-content">
        {/* Hero Banner Section — Full-Bleed Edge-to-Edge Banner (Fore Reference Layout) */}
        <section id="hero" className="relative w-screen max-w-full left-1/2 -translate-x-1/2">
          <div
            onClick={() => handleOrderCta()}
            onTouchStart={handleBannerTouchStart}
            onTouchEnd={handleBannerTouchEnd}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOrderCta();
              }
            }}
            className="relative z-10 w-full aspect-[16/10] sm:h-56 overflow-hidden rounded-b-3xl bg-[#F8FAFC] cursor-pointer select-none"
          >
            {heroBanners.map((slide, idx) => {
              const isCurrent = idx === currentSlide;
              return (
                <img
                  key={slide.id}
                  className={`w-full h-full object-cover object-center transition-opacity duration-500 ${
                    isCurrent
                      ? 'relative opacity-100 z-10'
                      : 'absolute inset-0 opacity-0 z-0 pointer-events-none'
                  }`}
                  src={slide.imageUrl}
                  alt={slide.alt}
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  onError={(e) => {
                    const target = e.currentTarget;
                    if (target.src !== DEFAULT_PRODUCT_IMAGE) {
                      target.src = DEFAULT_PRODUCT_IMAGE;
                    }
                  }}
                />
              );
            })}

            {/* Fore-style subtle carousel pagination dots above the overlapping greeting card */}
            {heroBanners.length > 1 && (
              <div className="absolute bottom-9 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center gap-1.5 px-2.5 py-1 rounded-full bg-black/20 backdrop-blur-[2px]">
                {heroBanners.map((slide, dotIdx) => {
                  const isActiveDot = dotIdx === currentSlide;
                  return (
                    <button
                      key={`hero-dot-${slide.id}`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentSlide(dotIdx);
                      }}
                      aria-label={`Slide ${dotIdx + 1}`}
                      className={`rounded-full transition-all duration-300 cursor-pointer ${
                        isActiveDot
                          ? 'w-4 h-1.5 bg-white'
                          : 'w-1.5 h-1.5 bg-white/60 hover:bg-white/90'
                      }`}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Greeting & Loyalty Compact Card — Floating & Overlapping Bottom of Banner */}
        <section id="loyalty-bar" className="relative z-30 -mt-7 px-4">
          <div className="bg-white border border-[#E2E8F0]/90 rounded-2xl p-4 shadow-[0_8px_24px_rgba(10,25,47,0.06)]">
            {/* Top Row: Dynamic Greeting & Gold Coins Illustration */}
            <div
              onClick={handleOpenMemberOrAccount}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenMemberOrAccount();
                }
              }}
              className="relative flex items-center justify-between gap-3 pb-3.5 cursor-pointer select-none overflow-hidden"
            >
              <p className="text-[17px] sm:text-lg font-extrabold text-[#0A192F] tracking-tight truncate pr-2">
                {customerProfile
                  ? customerDisplayName
                    ? `Hai, ${customerDisplayName.toUpperCase()}!`
                    : 'Hai, Teman Leton!'
                  : 'Hai, Teman Leton!'}
              </p>

              {/* Decorative Gold Coins Illustration */}
              <div className="relative shrink-0 h-8 w-24 flex items-center justify-end pointer-events-none" aria-hidden="true">
                <svg
                  viewBox="0 0 112 38"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-24 h-9 overflow-visible"
                >
                  <defs>
                    <linearGradient id="letonGoldOuter" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FFE066" />
                      <stop offset="50%" stopColor="#F59E0B" />
                      <stop offset="100%" stopColor="#D97706" />
                    </linearGradient>
                    <linearGradient id="letonGoldInner" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#FEF08A" />
                      <stop offset="60%" stopColor="#FBBF24" />
                      <stop offset="100%" stopColor="#B45309" />
                    </linearGradient>
                    <linearGradient id="letonCoinShine" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.65" />
                      <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {/* Soft warm glow behind coins */}
                  <ellipse cx="74" cy="24" rx="34" ry="12" fill="#FEF3C7" opacity="0.7" />

                  {/* Sparkle stars */}
                  <path
                    d="M18 11L19.2 14.3L22.5 15.5L19.2 16.7L18 20L16.8 16.7L13.5 15.5L16.8 14.3L18 11Z"
                    fill="#FBBF24"
                  />
                  <circle cx="34" cy="8" r="1.5" fill="#F59E0B" opacity="0.6" />
                  <circle cx="102" cy="9" r="1.5" fill="#FBBF24" opacity="0.8" />

                  {/* Back Left Tilted Coin */}
                  <g transform="translate(36, 8) rotate(-18 14 14)">
                    <circle cx="14" cy="14" r="13" fill="url(#letonGoldOuter)" />
                    <circle cx="14" cy="14" r="10" fill="url(#letonGoldInner)" stroke="#FEF08A" strokeWidth="0.8" />
                    <path
                      d="M14 9.5L15.3 12.3L18.4 12.7L16.1 14.8L16.7 17.9L14 16.4L11.3 17.9L11.9 14.8L9.6 12.7L12.7 12.3L14 9.5Z"
                      fill="#FFFBEB"
                      opacity="0.9"
                    />
                  </g>

                  {/* Back Right Coin */}
                  <g transform="translate(76, 6) rotate(16 15 15)">
                    <circle cx="15" cy="15" r="14" fill="url(#letonGoldOuter)" />
                    <circle cx="15" cy="15" r="10.8" fill="url(#letonGoldInner)" stroke="#FEF08A" strokeWidth="0.8" />
                    <circle cx="15" cy="15" r="6" fill="#F59E0B" opacity="0.35" />
                  </g>

                  {/* Front Center Main Gold Coin */}
                  <g transform="translate(54, 4)">
                    <circle cx="17" cy="17" r="16" fill="#D97706" opacity="0.25" />
                    <circle cx="16" cy="16" r="15.5" fill="url(#letonGoldOuter)" stroke="#FFFBEB" strokeWidth="1" />
                    <circle cx="16" cy="16" r="12" fill="url(#letonGoldInner)" stroke="#FEF08A" strokeWidth="0.9" />
                    <ellipse cx="13" cy="10" rx="7" ry="3.5" transform="rotate(-20 13 10)" fill="url(#letonCoinShine)" />
                    <path
                      d="M16 9.8L17.7 13.4L21.7 13.9L18.8 16.6L19.5 20.5L16 18.6L12.5 20.5L13.2 16.6L10.3 13.9L14.3 13.4L16 9.8Z"
                      fill="#FFFBEB"
                    />
                  </g>
                </svg>
              </div>
            </div>

            {/* Thin Divider Line */}
            <div className="border-t border-[#E2E8F0]" />

            {/* Bottom Row: 2 Side-by-Side Pill Buttons (Poin & Membership) */}
            <div className="pt-3 grid grid-cols-2 gap-2.5">
              {/* Left Pill: Poin */}
              <button
                type="button"
                onClick={handleOpenMemberOrAccount}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-full bg-white border border-[#E2E8F0] shadow-[0_2px_8px_rgba(10,25,47,0.04)] hover:border-[#0056D2]/40 transition-all cursor-pointer min-w-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0060E8] to-[#0142A5] text-white flex items-center justify-center shrink-0 shadow-2xs overflow-hidden">
                    {data?.siteSettings?.logoUrl ? (
                      <img
                        src={resolveMediaUrl(data.siteSettings.logoUrl)}
                        alt={data.siteSettings?.brandName || 'LetonKu'}
                        className="w-full h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <img
                        src="/logo_icon.jpg"
                        alt="LetonKu"
                        className="w-full h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                    )}
                  </span>
                  <span className="text-xs sm:text-[13px] font-bold text-[#0A192F] truncate">
                    {displayPoints} Poin
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-[#0A192F] shrink-0" />
              </button>

              {/* Right Pill: Membership Tier */}
              <button
                type="button"
                onClick={handleOpenMemberOrAccount}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-full bg-white border border-[#E2E8F0] shadow-[0_2px_8px_rgba(10,25,47,0.04)] hover:border-[#0056D2]/40 transition-all cursor-pointer min-w-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 shadow-2xs ${
                      tierData.tier === 'PLATINUM'
                        ? 'bg-gradient-to-br from-sky-400 via-indigo-500 to-slate-800 text-white'
                        : tierData.tier === 'GOLD'
                        ? 'bg-gradient-to-br from-amber-300 via-amber-500 to-yellow-600 text-white'
                        : 'bg-gradient-to-br from-slate-300 via-slate-400 to-slate-600 text-white'
                    }`}
                  >
                    <Star className="w-3.5 h-3.5 fill-current" />
                  </span>
                  <span className="text-xs sm:text-[13px] font-bold text-[#0A192F] truncate">
                    {customerProfile ? tierData.tierName || 'Silver' : 'Silver'}
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-[#0A192F] shrink-0" />
              </button>
            </div>

            {/* Previous CTA button removed */}
          </div>
        </section>

        {/* Order CTA Section ("Pesan Leton Sekarang?") */}
        <section className="mt-5 px-4">
          <div className="mb-2.5">
            <h3 className="font-bold text-sm sm:text-base text-[#0A192F] tracking-tight">
              Pesan Leton Sekarang?
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* 1. PICK UP CARD */}
            <button
              type="button"
              onClick={() => handleOrderCta(undefined, 'TAKE AWAY')}
              className="relative overflow-hidden rounded-2xl p-3.5 sm:p-4 min-h-[112px] sm:min-h-[122px] bg-gradient-to-br from-white via-[#F4F9FF] to-[#E6F1FE] border border-[#93C5FD]/80 hover:border-[#0056D2]/60 shadow-[0_2px_10px_rgba(0,86,210,0.05)] hover:shadow-md flex flex-col justify-between text-left transition-all cursor-pointer group active:scale-[0.99]"
            >
              {/* Decorative background bubbles */}
              <div className="pointer-events-none absolute right-10 top-3 w-3.5 h-3.5 rounded-full bg-[#0056D2]/[0.06]" />
              <div className="pointer-events-none absolute right-5 top-5 w-2 h-2 rounded-full bg-[#0056D2]/[0.08]" />
              <div className="pointer-events-none absolute right-8 top-8 w-2.5 h-2.5 rounded-full bg-[#0056D2]/[0.05]" />

              <div className="relative z-10 pr-10 sm:pr-12">
                <span className="block text-base sm:text-[18px] font-extrabold text-[#0056D2] tracking-tight leading-tight">
                  Pick Up
                </span>
                <p className="mt-1 text-[11px] sm:text-xs text-[#475569] font-medium leading-snug">
                  Ambil di outlet tanpa antri
                </p>
              </div>

              {/* Bottom-right illustration */}
              <div
                className="pointer-events-none absolute bottom-2 right-2.5 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#D8EAFF]/90 border border-[#BFDBFE]/80 flex items-center justify-center group-hover:scale-105 transition-transform"
                aria-hidden="true"
              >
                <div className="relative flex items-center justify-center text-[#0056D2]">
                  <Store className="w-6 h-6 sm:w-7 sm:h-7 stroke-[2]" />
                  <span className="absolute -bottom-1 -left-1.5 bg-white border border-[#93C5FD] text-[#0056D2] rounded-full w-4 h-4 flex items-center justify-center shadow-2xs text-[9px] font-black">
                    ✓
                  </span>
                </div>
              </div>
            </button>

            {/* 2. DELIVERY CARD */}
            <button
              type="button"
              disabled={isCheckingDeliveryStatus || globalDeliveryStatus !== true}
              aria-disabled={isCheckingDeliveryStatus || globalDeliveryStatus !== true}
              onClick={() => {
                if (isCheckingDeliveryStatus) return;
                if (globalDeliveryStatus === true) {
                  handleOrderCta(undefined, 'DELIVERY');
                }
              }}
              className={`relative overflow-hidden rounded-2xl p-3.5 sm:p-4 min-h-[112px] sm:min-h-[122px] border flex flex-col justify-between text-left transition-all ${
                globalDeliveryStatus === false || globalDeliveryStatus === 'UNKNOWN' || isCheckingDeliveryStatus
                  ? 'bg-gradient-to-br from-slate-50 via-slate-100/80 to-slate-200/60 border-slate-200/90 cursor-not-allowed'
                  : 'bg-gradient-to-br from-white via-[#F0F9FF] to-[#DFF2FE] border-[#7DD3FC]/80 hover:border-[#0284C7]/60 shadow-[0_2px_10px_rgba(2,132,199,0.05)] hover:shadow-md cursor-pointer group active:scale-[0.99]'
              }`}
            >
              {/* Decorative background bubbles */}
              <div
                className={`pointer-events-none absolute right-10 top-3 w-3.5 h-3.5 rounded-full ${
                  globalDeliveryStatus === true && !isCheckingDeliveryStatus
                    ? 'bg-[#0284C7]/[0.06]'
                    : 'bg-slate-400/[0.08]'
                }`}
              />
              <div
                className={`pointer-events-none absolute right-5 top-5 w-2 h-2 rounded-full ${
                  globalDeliveryStatus === true && !isCheckingDeliveryStatus
                    ? 'bg-[#0284C7]/[0.08]'
                    : 'bg-slate-400/[0.1]'
                }`}
              />
              <div
                className={`pointer-events-none absolute right-8 top-8 w-2.5 h-2.5 rounded-full ${
                  globalDeliveryStatus === true && !isCheckingDeliveryStatus
                    ? 'bg-[#0284C7]/[0.05]'
                    : 'bg-slate-400/[0.07]'
                }`}
              />

              <div className="relative z-10 pr-10 sm:pr-12">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className={`text-base sm:text-[18px] font-extrabold tracking-tight leading-tight ${
                      globalDeliveryStatus === false || globalDeliveryStatus === 'UNKNOWN' || isCheckingDeliveryStatus
                        ? 'text-slate-500'
                        : 'text-[#026AA7]'
                    }`}
                  >
                    Delivery
                  </span>
                  {isCheckingDeliveryStatus ? (
                    <span className="px-1.5 py-0.5 rounded-md text-[8px] sm:text-[9px] font-mono font-bold bg-slate-200 text-slate-600 uppercase tracking-wider">
                      Cek...
                    </span>
                  ) : globalDeliveryStatus === false ? (
                    <span className="px-1.5 py-0.5 rounded-md text-[8px] sm:text-[9px] font-mono font-bold bg-rose-100 text-rose-700 border border-rose-200/70 uppercase tracking-wider">
                      NONAKTIF
                    </span>
                  ) : globalDeliveryStatus === 'UNKNOWN' ? (
                    <span className="px-1.5 py-0.5 rounded-md text-[8px] sm:text-[9px] font-mono font-bold bg-amber-100 text-amber-700 uppercase tracking-wider">
                      OFFLINE
                    </span>
                  ) : null}
                </div>
                <p
                  className={`mt-1 text-[11px] sm:text-xs font-medium leading-snug ${
                    globalDeliveryStatus === false || globalDeliveryStatus === 'UNKNOWN' || isCheckingDeliveryStatus
                      ? 'text-slate-400'
                      : 'text-[#475569]'
                  }`}
                >
                  {isCheckingDeliveryStatus
                    ? 'Memeriksa layanan antar...'
                    : globalDeliveryStatus === false
                    ? 'Layanan sedang dinonaktifkan'
                    : globalDeliveryStatus === 'UNKNOWN'
                    ? 'Sedang dalam pemeriksaan'
                    : 'Pesan antar sampai tujuan'}
                </p>
              </div>

              {/* Bottom-right illustration */}
              <div
                className={`pointer-events-none absolute bottom-2 right-2.5 w-12 h-12 sm:w-14 sm:h-14 rounded-full border flex items-center justify-center transition-transform ${
                  globalDeliveryStatus === false || globalDeliveryStatus === 'UNKNOWN' || isCheckingDeliveryStatus
                    ? 'bg-slate-200/80 border-slate-300/80 text-slate-400'
                    : 'bg-[#D0ECFE]/90 border-[#7DD3FC]/80 text-[#026AA7] group-hover:scale-105'
                }`}
                aria-hidden="true"
              >
                <div className="relative flex items-center justify-center">
                  <Motorbike className="w-6 h-6 sm:w-7 sm:h-7 stroke-[2]" />
                  <span
                    className={`absolute -top-1 -left-1.5 rounded-full p-0.5 shadow-2xs flex items-center justify-center border ${
                      globalDeliveryStatus === false || globalDeliveryStatus === 'UNKNOWN' || isCheckingDeliveryStatus
                        ? 'bg-slate-100 border-slate-300 text-slate-400'
                        : 'bg-white border-[#7DD3FC] text-[#026AA7]'
                    }`}
                  >
                    <Package className="w-2.5 h-2.5 stroke-[2.3]" />
                  </span>
                </div>
              </div>
            </button>
          </div>
        </section>

        <SpecialForYouSection />

        {/* Categories Section */}
        <section id="categories" className="mt-6">
          <div className="px-4 flex justify-between items-center mb-3">
            <h2 className="font-bold text-base text-[#0A192F]">Categories</h2>
            <button
              type="button"
              onClick={scrollToMenuSection}
              className="text-xs font-semibold text-[#0056D2] cursor-pointer"
            >
              See All
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto px-4 hide-scrollbar">
            <button
              type="button"
              onClick={() => setSelectedCategory('all')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs whitespace-nowrap transition-all cursor-pointer ${
                selectedCategory === 'all'
                  ? 'bg-[#0056D2] text-white shadow-md shadow-[#0056D2]/10'
                  : 'bg-[#F8FAFC] text-[#0A192F] border border-[#E2E8F0]'
              }`}
            >
              <Coffee className="w-3.5 h-3.5" />
              <span>All Coffee</span>
            </button>
            {sortedCategories.map((cat, idx) => {
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-xs whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#0056D2] text-white shadow-md shadow-[#0056D2]/10'
                      : 'bg-[#F8FAFC] text-[#0A192F] border border-[#E2E8F0]'
                  }`}
                >
                  {getCategoryIcon(cat.name, idx)}
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Popular Products Section */}
        <section id="popular" className="px-4 mt-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-base text-[#0A192F]">Popular Near You</h2>
            <button
              type="button"
              onClick={scrollToMenuSection}
              aria-label="Filter Menu"
              className="w-8 h-8 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-slate-600 cursor-pointer"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {favoriteItems.map((item) => {
              const resolvedImg = resolveMediaUrl(item.image) || DEFAULT_PRODUCT_IMAGE;
              const isLiked = Boolean(favoriteIds[item.id]);

              return (
                <div
                  key={`fav-${item.id}`}
                  onClick={() => handleOrderCta(item)}
                  className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                >
                  <div className="h-36 bg-[#F8FAFC] relative overflow-hidden">
                    <img
                      className="w-full h-full object-cover"
                      src={resolvedImg}
                      alt={item.name}
                      loading="lazy"
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (target.src !== DEFAULT_PRODUCT_IMAGE) {
                          target.src = DEFAULT_PRODUCT_IMAGE;
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFavoriteIds((prev) => ({
                          ...prev,
                          [item.id]: !prev[item.id],
                        }));
                      }}
                      aria-label="Simpan Favorit"
                      className={`absolute top-2 right-2 w-7 h-7 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                        isLiked ? 'text-red-500' : 'text-slate-400 hover:text-red-500'
                      }`}
                    >
                      <Heart
                        className={`w-3.5 h-3.5 ${isLiked ? 'fill-red-500' : ''}`}
                      />
                    </button>
                  </div>
                  <div className="p-3 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-bold text-sm truncate text-[#0A192F]">
                        {item.name}
                      </h3>
                      <p className="text-[10px] text-slate-400 mb-2 truncate">
                        {item.description || 'Signature Espresso'}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-[#0056D2]">
                        {formatRupiah(item.price)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrderCta(item);
                        }}
                        aria-label={`Tambah ${item.name}`}
                        className="w-7 h-7 bg-[#0A192F] text-white rounded-lg flex items-center justify-center hover:bg-[#0056D2] transition-colors cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
};
